import React, { useState, useEffect, useMemo } from 'react';
import { FaTrash, FaCheck, FaChevronDown, FaChevronRight, FaUser } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const ResponsibleContacts = ({ onSave, initialData = {} }) => {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState(initialData.contacts || []);
  const [expandedRoles, setExpandedRoles] = useState(new Set());
  const [formData, setFormData] = useState({});

  const requiredRoles = useMemo(() => [
    t('workflow.onboarding.responsibleContacts.roles.esgLead'),
    t('workflow.onboarding.responsibleContacts.roles.it')
  ], [t]);
  const recommendedRoles = useMemo(() => [], [t]);
  const optionalRoles = useMemo(() => [], [t]);

  const allRoles = useMemo(() => [...requiredRoles, ...recommendedRoles, ...optionalRoles], [requiredRoles, recommendedRoles, optionalRoles]);

    // Initialize form data for each role
  useEffect(() => {
    // Try to load data from localStorage first
    try {
      const savedData = localStorage.getItem('onboardingData');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (parsedData.responsibleContacts && parsedData.responsibleContacts.contacts) {
          const savedContacts = parsedData.responsibleContacts.contacts;
          setContacts(savedContacts);
          
          // Initialize form data with loaded contacts
          const initialFormData = {};
          allRoles.forEach(role => {
            const existingContact = savedContacts.find(c => c.role === role);
            initialFormData[role] = {
              full_name: existingContact?.full_name || '',
              email: existingContact?.email || '',
              phone: existingContact?.phone || '',
              language: existingContact?.language || 'PL',
              timezone: existingContact?.timezone || 'Europe/Warsaw'
            };
          });
          setFormData(initialFormData);
        } else {
          // Initialize empty form data if no saved contacts
          const initialFormData = {};
          allRoles.forEach(role => {
            initialFormData[role] = {
              full_name: '',
              email: '',
              phone: '',
              language: 'PL',
              timezone: 'Europe/Warsaw'
            };
          });
          setFormData(initialFormData);
        }
      } else {
        // Initialize empty form data if no saved data
        const initialFormData = {};
        allRoles.forEach(role => {
          initialFormData[role] = {
            full_name: '',
            email: '',
            phone: '',
            language: 'PL',
            timezone: 'Europe/Warsaw'
          };
        });
        setFormData(initialFormData);
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      
      // Initialize empty form data on error
      const initialFormData = {};
      allRoles.forEach(role => {
        initialFormData[role] = {
          full_name: '',
          email: '',
          phone: '',
          language: 'PL',
          timezone: 'Europe/Warsaw'
        };
      });
      setFormData(initialFormData);
    }
  }, [allRoles]);

  const getRoleStatus = (role) => {
    const contact = contacts.find(c => c.role === role);
    return {
      assigned: !!contact,
      required: requiredRoles.includes(role),
      recommended: recommendedRoles.includes(role),
      optional: optionalRoles.includes(role)
    };
  };

  const handleInputChange = (role, field, value) => {
    setFormData(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [field]: value
      }
    }));
  };



  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateRoleForm = (role) => {
    const data = formData[role];
    if (!data.full_name.trim()) return t('workflow.onboarding.responsibleContacts.confirmations.validationErrors.fullNameRequired');
    if (!data.email.trim()) return t('workflow.onboarding.responsibleContacts.confirmations.validationErrors.emailRequired');
    if (!validateEmail(data.email)) return t('workflow.onboarding.responsibleContacts.confirmations.validationErrors.invalidEmail');
    return null;
  };

  const handleSaveContact = (role) => {
    const error = validateRoleForm(role);
    if (error) {
      alert(error);
      return;
    }

    const contactData = {
      id: Date.now() + Math.random(), // Simple ID generation
      role,
      ...formData[role]
    };

    // Remove existing contact with same role if exists
    const updatedContacts = contacts.filter(c => c.role !== role);
    updatedContacts.push(contactData);
    
    setContacts(updatedContacts);
    onSave && onSave({ contacts: updatedContacts });
    
    
    
    // Auto-save to localStorage
    try {
      const onboardingData = {
        responsibleContacts: { contacts: updatedContacts },
        lastUpdated: new Date().toISOString(),
        step: 'responsibleContacts'
      };
      
      const existingData = localStorage.getItem('onboardingData');
      const allOnboardingData = existingData ? JSON.parse(existingData) : {};
      
      const updatedData = {
        ...allOnboardingData,
        ...onboardingData
      };
      
      localStorage.setItem('onboardingData', JSON.stringify(updatedData));
      
    } catch (error) {
      console.error('Error auto-saving to localStorage:', error);
    }
    
    // Collapse the role section after saving
    setExpandedRoles(prev => {
      const newSet = new Set(prev);
      newSet.delete(role);
      return newSet;
    });
  };

    const handleDeleteContact = (role) => {
    if (window.confirm(t('workflow.onboarding.responsibleContacts.confirmations.deleteContact'))) {
      const updatedContacts = contacts.filter(c => c.role !== role);
      setContacts(updatedContacts);
      onSave && onSave({ contacts: updatedContacts });
      
      // Auto-save to localStorage after deletion
      try {
        const onboardingData = {
          responsibleContacts: { contacts: updatedContacts },
          lastUpdated: new Date().toISOString(),
          step: 'responsibleContacts'
        };
        
        const existingData = localStorage.getItem('onboardingData');
        const allOnboardingData = existingData ? JSON.parse(existingData) : {};
        
        const updatedData = {
          ...allOnboardingData,
          ...onboardingData
        };
        
        localStorage.setItem('onboardingData', JSON.stringify(updatedData));
        
      } catch (error) {
        console.error('Error saving deletion to localStorage:', error);
      }
      
      // Reset form data for this role
      setFormData(prev => ({
        ...prev,
                 [role]: {
           full_name: '',
           email: '',
           phone: '',
           language: 'PL',
           timezone: 'Europe/Warsaw'
         }
      }));
    }
  };

     

  const toggleRoleExpansion = (role) => {
    setExpandedRoles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(role)) {
        newSet.delete(role);
      } else {
        newSet.add(role);
      }
      return newSet;
    });
  };

  // Completion is handled by parent component

  const renderRoleSection = (role) => {
    const status = getRoleStatus(role);
    const isExpanded = expandedRoles.has(role);
    const contact = contacts.find(c => c.role === role);
    const data = formData[role] || {};

         return (
       <div key={role} className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
        {/* Role Header */}
        <div 
          className={`p-4 cursor-pointer transition-colors ${
            isExpanded ? 'bg-blue-50 border-b border-gray-200' : 'bg-white hover:bg-gray-50'
          }`}
          onClick={() => toggleRoleExpansion(role)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                status.assigned 
                  ? 'bg-green-100 text-green-600' 
                  : status.required 
                    ? 'bg-red-100 text-red-600'
                    : 'bg-gray-100 text-gray-600'
              }`}>
                {status.assigned ? <FaCheck className="w-4 h-4" /> : <FaUser className="w-4 h-4" />}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{role}</h3>
                <div className="flex items-center space-x-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    status.required 
                      ? 'bg-red-100 text-red-700' 
                      : status.recommended 
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-700'
                  }`}>
                    {status.required ? t('workflow.onboarding.responsibleContacts.status.required') : status.recommended ? t('workflow.onboarding.responsibleContacts.status.recommended') : t('workflow.onboarding.responsibleContacts.status.optional')}
                  </span>
                  {contact && (
                    <span className="text-sm text-gray-600">
                      {contact.full_name} • {contact.email}
                    </span>
                  )}
                </div>
              </div>
            </div>
                         <div className="flex items-center space-x-2">
               {contact && (
                 <button
                   onClick={(e) => {
                     e.stopPropagation();
                     handleDeleteContact(role);
                   }}
                   className="text-red-600 hover:text-red-800"
                 >
                   <FaTrash />
                 </button>
               )}
              {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
            </div>
          </div>
        </div>

                 {/* Expanded Form */}
         {isExpanded && (
           <div className="p-4 bg-gray-50">
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSaveContact(role);
                         }} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('workflow.onboarding.responsibleContacts.form.fullName')}
                  </label>
                  <input
                    type="text"
                    value={data.full_name || ''}
                    onChange={(e) => handleInputChange(role, 'full_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('workflow.onboarding.responsibleContacts.form.email')}
                  </label>
                  <input
                    type="email"
                    value={data.email || ''}
                    onChange={(e) => handleInputChange(role, 'email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('workflow.onboarding.responsibleContacts.form.emailPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('workflow.onboarding.responsibleContacts.form.phone')}
                  </label>
                  <input
                    type="tel"
                    value={data.phone || ''}
                    onChange={(e) => handleInputChange(role, 'phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('workflow.onboarding.responsibleContacts.form.language')}
                  </label>
                  <select
                    value={data.language || 'PL'}
                    onChange={(e) => handleInputChange(role, 'language', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="PL">{t('workflow.onboarding.responsibleContacts.languages.polish')}</option>
                    <option value="EN">{t('workflow.onboarding.responsibleContacts.languages.english')}</option>
                    <option value="DE">{t('workflow.onboarding.responsibleContacts.languages.german')}</option>
                    <option value="FR">{t('workflow.onboarding.responsibleContacts.languages.french')}</option>
                  </select>
                </div>

                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('workflow.onboarding.responsibleContacts.form.timezone')}
                   </label>
                   <select
                     value={data.timezone || 'Europe/Warsaw'}
                     onChange={(e) => handleInputChange(role, 'timezone', e.target.value)}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                     <option value="Europe/Warsaw">{t('workflow.onboarding.responsibleContacts.timezones.europeWarsaw')}</option>
                     <option value="Europe/London">{t('workflow.onboarding.responsibleContacts.timezones.europeLondon')}</option>
                     <option value="Europe/Berlin">{t('workflow.onboarding.responsibleContacts.timezones.europeBerlin')}</option>
                     <option value="America/New_York">{t('workflow.onboarding.responsibleContacts.timezones.americaNewYork')}</option>
                   </select>
                 </div>
               </div>

               

                             <div className="flex justify-end space-x-3 pt-3">
                <button
                  type="button"
                  onClick={() => toggleRoleExpansion(role)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('workflow.onboarding.responsibleContacts.form.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {contact ? t('workflow.onboarding.responsibleContacts.form.updateContact') : t('workflow.onboarding.responsibleContacts.form.addContact')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="space-y-4">
          {allRoles.map(role => renderRoleSection(role))}
        </div>


      </div>
    </div>
  );
};

export default ResponsibleContacts;

